import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, ButtonBase, Typography } from '@mui/material'

import {
  DASHED_MOTIVATION_SOURCES,
  MOTIVATION_SOURCE_LABEL_KEYS,
  MOTIVATION_SOURCE_ORDER,
  useMotivationSourceColors,
} from '../../helpers/motivationSourceColors'
import { focusRingSx } from '../../helpers/styles'
import { MotivationSource } from '../../types'

interface MotivationSourceLegendProps {
  /**
   * Sources whose data is available for the current game and should therefore
   * be rendered as legend entries. Defaults to the canonical render order so
   * legacy callers that don't pass the prop keep the previous behaviour. Pass
   * a narrower list when a game lacks one of the series (e.g. no Game & Live
   * Events profile), so the legend doesn't advertise a toggle for a series
   * the charts don't render.
   */
  availableSources?: readonly MotivationSource[]
  /** Sources currently toggled off — receives a strikethrough/faded swatch. */
  hiddenSources: ReadonlySet<MotivationSource>
  /** Click handler that should toggle the supplied source in `hiddenSources`. */
  onToggle: (source: MotivationSource) => void
}

/**
 * Shared legend rendered once below the radar + bar chart pair so the two
 * charts read as a single visualisation. Mirrors the line-style legend used by
 * Chart.js (a thin horizontal stroke per source, dashed for Game Design) and
 * preserves the click-to-toggle behaviour: the parent owns `hiddenSources` and
 * the charts react to it by hiding the matching dataset.
 */
export const MotivationSourceLegend: FC<MotivationSourceLegendProps> = ({ availableSources = MOTIVATION_SOURCE_ORDER, hiddenSources, onToggle }) => {
  const { t } = useTranslation('ua-insights')
  const sourceColors = useMotivationSourceColors()

  // Render in the canonical chart order rather than the order callers happen
  // to pass `availableSources` in, so the legend stays visually stable when a
  // source becomes available/unavailable across games.
  const sourcesToRender = MOTIVATION_SOURCE_ORDER.filter((source) => availableSources.includes(source))

  if (sourcesToRender.length === 0) return null

  return (
    <Box
      role="list"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        gap: { xs: 2, sm: 3.5 },
        mt: 1.5,
      }}
    >
      {sourcesToRender.map((source) => {
        const color = sourceColors[source].stroke
        const isDashed = DASHED_MOTIVATION_SOURCES.has(source)
        const isHidden = hiddenSources.has(source)
        const label = t(MOTIVATION_SOURCE_LABEL_KEYS[source])
        return (
          <Box key={source} role="listitem" sx={{ display: 'flex' }}>
            <ButtonBase
              onClick={() => onToggle(source)}
              aria-pressed={!isHidden}
              aria-label={t(isHidden ? 'legend_show_source' : 'legend_hide_source', { source: label })}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                cursor: 'pointer',
                opacity: isHidden ? 0.4 : 1,
                transition: 'opacity 0.15s, background-color 0.15s',
                '&:hover': { backgroundColor: 'action.hover' },
                ...focusRingSx(),
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 11,
                  height: 11,
                  borderRadius: '50%',
                  border: isDashed ? `2px dashed ${color}` : 'none',
                  backgroundColor: isDashed ? 'transparent' : color,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: 13,
                  color: 'text.primary',
                  textDecoration: isHidden ? 'line-through' : 'none',
                  userSelect: 'none',
                }}
              >
                {label}
              </Typography>
            </ButtonBase>
          </Box>
        )
      })}
    </Box>
  )
}
